import { useState, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { MeetingComment } from '../interfaces/interface';
import { SchedulingService } from '../services/SchedulingService';
import { EmployeeSettingsService } from '../services/EmployeeSettingsService';
import { NotifyMeetingParticipants } from '../utils/notifyCreator';
import { logger } from 'src/logger';

interface UseTimeSlotCommentsProps {
  meetingId: string | null;
  slotId: string;
  creatorAlias: string | null;
  currentUserAlias: string;
  meetingSubject?: string;
  slotLabel?: string;
}

/**
 * Manages per-slot comment state: lazy load, optimistic add/edit.
 * Comments are stored at: MEETING#<meetingId>#TIME_SLOT#<slotId>#COMMENTS
 */
export const useTimeSlotComments = ({ meetingId, slotId, creatorAlias, currentUserAlias, meetingSubject, slotLabel }: UseTimeSlotCommentsProps) => {
  const [comments, setComments] = useState<MeetingComment[]>([]);
  const [commentCount, setCommentCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isCountLoaded, setIsCountLoaded] = useState(false);

  // Lightweight: load only the count on mount
  const loadCommentCount = useCallback(async () => {
    if (!meetingId || !creatorAlias || isCountLoaded) return;
    try {
      const loaded = await SchedulingService.getTimeSlotComments(creatorAlias, meetingId, slotId);
      setCommentCount(loaded.length);
      setIsCountLoaded(true);
    } catch {
      // silent — count stays 0
    }
  }, [meetingId, creatorAlias, slotId, isCountLoaded]);

  const loadComments = useCallback(async () => {
    if (!meetingId || !creatorAlias || isLoaded) return;
    setIsLoading(true);
    try {
      const loaded = await SchedulingService.getTimeSlotComments(creatorAlias, meetingId, slotId);
      setComments(loaded);
      setCommentCount(loaded.length);
      setIsLoaded(true);
      setIsCountLoaded(true);
    } catch (error) {
      logger.error('Failed to load time slot comments', { error, meetingId, slotId });
    } finally {
      setIsLoading(false);
    }
  }, [meetingId, creatorAlias, slotId, isLoaded]);

  const addComment = useCallback(
    async (content: string, parentCommentId?: string) => {
      if (!meetingId || !creatorAlias || !content.trim()) return;

      let employeeName = currentUserAlias;
      try {
        const cached = await EmployeeSettingsService.getEmployeeInfo(currentUserAlias);
        if (cached?.employee_name) employeeName = cached.employee_name;
      } catch {
        // silent fallback
      }

      const newComment: MeetingComment = {
        commentId: uuidv4(),
        parentCommentId,
        loginName: currentUserAlias,
        employeeName,
        content: content.trim(),
        createdAt: new Date().toISOString()
      };

      setComments((prev) => [...prev, newComment].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()));
      setCommentCount((prev) => prev + 1);
      setIsLoaded(true);

      setIsSaving(true);
      try {
        await SchedulingService.addTimeSlotComment(creatorAlias, meetingId, slotId, newComment);
        if (creatorAlias) {
          NotifyMeetingParticipants({
            type: parentCommentId ? 'reply' : 'comment',
            actorLogin: currentUserAlias,
            actorName: employeeName,
            creatorAlias,
            meetingId,
            meetingSubject: meetingSubject || 'Untitled',
            slotId,
            slotLabel,
            commentPreview: content.trim().slice(0, 100)
          });
        }
      } catch (error) {
        logger.error('Failed to add time slot comment', { error, meetingId, slotId });
        setComments((prev) => prev.filter((c) => c.commentId !== newComment.commentId));
        setCommentCount((prev) => Math.max(0, prev - 1));
      } finally {
        setIsSaving(false);
      }
    },
    [meetingId, creatorAlias, slotId, currentUserAlias, meetingSubject, slotLabel]
  );

  const editComment = useCallback(
    async (commentId: string, newContent: string) => {
      if (!meetingId || !creatorAlias || !newContent.trim()) return;

      const now = new Date().toISOString();

      let previousComments: MeetingComment[] = [];
      setComments((prev) => {
        previousComments = prev;
        return prev.map((c) => (c.commentId === commentId ? { ...c, content: newContent.trim(), updatedAt: now } : c));
      });

      try {
        await SchedulingService.editTimeSlotComment(creatorAlias, meetingId, slotId, commentId, newContent.trim());
      } catch (error) {
        logger.error('Failed to edit time slot comment', { error, meetingId, slotId, commentId });
        setComments(previousComments);
      }
    },
    [meetingId, creatorAlias, slotId]
  );

  const topLevelComments = comments.filter((comment) => !comment.parentCommentId);
  const getReplies = (parentId: string) => comments.filter((comment) => comment.parentCommentId === parentId);

  return {
    comments,
    commentCount,
    topLevelComments,
    getReplies,
    isLoading,
    isSaving,
    isLoaded,
    loadCommentCount,
    loadComments,
    addComment,
    editComment
  };
};
